import { useEffect, useState } from 'react';
import type { VacationRequest } from '../models/vacation.types';
import {
    isDraftVacationRequest,
    resolveDraftGraceEnd,
} from '../utils/vacationRequestStatus';

const TICK_MS = 1000;

export interface DraftCountdown {
    remainingMs: number;
    remainingLabel: string;
    isExpired: boolean;
}

function formatRemaining(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Contador que tick a cada segundo. Não ativa timers para abrir a solicitação — isso vem de
 * `useDraftAutoOpen`. Este hook só drive a UI: botão de excluir, contador visual no card e modal.
 * Guarda o relógio em estado (`now`) e deriva o resultado (`remainingMs`) no render, para evitar
 * o efeito de "lag" que vinha de ler dois dados de origem diferente — um estado antigo + um render
 * novo — que causava disparos falsos em transições.
 */
export function useDraftCountdown(request: VacationRequest | null): DraftCountdown {
    const graceEnd = request && isDraftVacationRequest(request) ? resolveDraftGraceEnd(request) : null;
    const graceEndMs = graceEnd?.getTime() ?? null;

    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (graceEndMs === null) return;

        const tick = () => setNow(Date.now());
        tick();

        const intervalId = setInterval(tick, TICK_MS);
        return () => clearInterval(intervalId);
    }, [graceEndMs]);

    const remainingMs = graceEndMs === null ? 0 : Math.max(0, graceEndMs - now);

    return {
        remainingMs,
        remainingLabel: formatRemaining(remainingMs),
        isExpired: graceEndMs === null || remainingMs <= 0,
    };
}
